/* ==========================================================================
   GSJ Live Prices — shared data layer + renderers

   Used by the Cash for Gold strip (gsj-live-prices-strip) AND by every live
   section on the Live Prices page (gsj-lp-hero / -gold-tables / -silver-tables
   / -calculator). One script, one set of rules.

   ── ONE INTERNAL CONTRACT ────────────────────────────────────────────────
   Every renderer reads this object and nothing else, so the data source can
   change without touching render code:

     { updated_at, mode: "direct" | "engine",
       spot:   { gold_cad_g, gold_cad_oz, silver_cad_g, silver_cad_oz, usdcad },
       gold:   [ { karat, purity, premium, other } ],
       silver: [ { purity_label, purity, premium, other } ],
       status: { fresh, market_open } }

   ── TWO MODES ────────────────────────────────────────────────────────────
   DIRECT (default, whenever the endpoint setting is blank): fetches spot from
   api.gold-api.com (USD/oz, keyless) and the CAD rate from open.er-api.com —
   both send Access-Control-Allow-Origin: * — then computes every row in the
   browser as spot_cad_g × purity × payout%, rounded UP to the cent.

   ENGINE (endpoint setting filled): GETs that URL, which returns exactly the
   contract above, and renders it identically. That is the ONLY difference.

   ── FETCHING POLICY ──────────────────────────────────────────────────────
   sessionStorage cache (5 min) so navigating the site does not refetch. One
   shared Feed per page, so four live sections make one request, not four.
   Nothing is requested until an IntersectionObserver says a section is near
   the viewport; the 60s refresh runs only while the tab is visible AND a
   section is in view; everything is torn down on pagehide.

   Vanilla, deferred, no dependencies, no requestAnimationFrame loops.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-gsj-live-prices]';
  var PLACEHOLDER = '$—';
  var FETCH_TIMEOUT_MS = 8000;
  var CACHE_KEY = 'gsj-lp-v1';
  var CACHE_TTL_MS = 5 * 60 * 1000;
  var TROY_OUNCE_GRAMS = 31.1034768;

  /* Purities are fixed metal facts, not merchant settings. */
  var GOLD_KARATS = [[24, 0.999], [22, 0.916], [21, 0.875], [18, 0.75], [14, 0.585], [10, 0.417], [9, 0.375]];
  var SILVER_PURITIES = [['999 Fine', 0.999], ['925 Sterling', 0.925], ['800 Silver', 0.8]];

  /* A reading outside these is treated as a failed fetch and never rendered. */
  var BOUNDS = {
    gold_usd_oz: { min: 1500, max: 10000 },
    silver_usd_oz: { min: 15, max: 200 },
    usdcad: { min: 1.1, max: 1.7 }
  };

  var API = {
    gold: 'https://api.gold-api.com/price/XAU',
    silver: 'https://api.gold-api.com/price/XAG',
    fx: 'https://open.er-api.com/v6/latest/USD'
  };

  var instances = Object.create(null);

  /* ----------------------------------------------------------- helpers --- */

  var currency = (function () {
    try {
      return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: 'CAD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    } catch (error) {
      return null;
    }
  })();

  function money(value) {
    if (typeof value !== 'number' || !isFinite(value)) return PLACEHOLDER;
    if (currency) return currency.format(value);
    return '$' + value.toFixed(2);
  }

  /**
   * Round UP to the next cent. Naive Math.ceil(n * 100) / 100 is wrong in
   * binary floating point — 1.23 * 100 is 123.00000000000001, which would round
   * a clean $1.23 up to $1.24 — so the multiply is snapped to 6 decimals first.
   * That kills the representation noise while leaving a genuine sub-cent
   * remainder intact so it still rounds up.
   */
  function roundUpCents(value) {
    if (typeof value !== 'number' || !isFinite(value) || value <= 0) return 0;
    return Math.ceil(Number((value * 100).toFixed(6))) / 100;
  }

  /** "8:05 AM" in the given zone. en-CA renders "8:05 a.m."; strip the dots. */
  function clockLabel(date, timezone) {
    var text;
    try {
      text = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).format(date);
    } catch (error) {
      text = date.toLocaleTimeString();
    }
    return text.toUpperCase().replace(/\./g, '').replace(/\s+/g, ' ').trim();
  }

  /**
   * Spot metals trade Sunday ~18:00 ET through Friday ~17:00 ET. Outside that
   * the feed's own timestamp is legitimately hours old and the prices are still
   * the right ones to show — the page must not blank on a weekend, which is
   * when people actually sit down to research selling.
   */
  function marketOpen(date) {
    var weekday;
    var hour;
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        weekday: 'short',
        hour: 'numeric',
        hour12: false
      }).formatToParts(date);
      for (var i = 0; i < parts.length; i += 1) {
        if (parts[i].type === 'weekday') weekday = parts[i].value;
        if (parts[i].type === 'hour') hour = Number(parts[i].value);
      }
    } catch (error) {
      return true; // No Intl timezone support: assume open rather than mislabel.
    }
    if (weekday === 'Sat') return false;
    if (weekday === 'Fri' && hour >= 17) return false;
    if (weekday === 'Sun' && hour < 18) return false;
    return true;
  }

  function toNumber(value) {
    if (typeof value === 'number') return isFinite(value) ? value : null;
    if (typeof value !== 'string') return null;
    var text = value.replace(/\s/g, '');
    if (text === '' || text.indexOf('-') !== -1) return null;
    text = text.replace(/[^0-9.,]/g, '');
    if (text === '') return null;
    if (text.indexOf('.') === -1) {
      var parts = text.split(',');
      if (parts.length === 2 && parts[1].length > 0 && parts[1].length <= 2) {
        text = parts[0] + '.' + parts[1];
      } else {
        text = parts.join('');
      }
    } else {
      text = text.replace(/,/g, '');
    }
    if (!/^[0-9]+(\.[0-9]*)?$/.test(text)) return null;
    var parsed = Number(text);
    return isFinite(parsed) ? parsed : null;
  }

  function normaliseLabel(value) {
    return String(value == null ? '' : value).toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  function setText(node, text) {
    if (node && node.textContent !== text) node.textContent = text;
  }

  function pickNumber(source, keys) {
    if (!source || typeof source !== 'object') return null;
    for (var i = 0; i < keys.length; i += 1) {
      var raw = source[keys[i]];
      if (typeof raw === 'number' && isFinite(raw)) return raw;
      if (typeof raw === 'string') {
        var parsed = Number(raw.trim());
        if (isFinite(parsed)) return parsed;
      }
    }
    return null;
  }

  function withinBounds(value, bound) {
    return typeof value === 'number' && isFinite(value) && value >= bound.min && value <= bound.max;
  }

  /* -------------------------------------------------------- config read --- */

  /**
   * Endpoint and payout percentages are theme settings, rendered onto every
   * live root as data attributes. They are identical on each root, so the first
   * one found wins.
   */
  function readConfig() {
    var root = document.querySelector(ROOT_SELECTOR);
    var read = function (name, fallback) {
      if (!root) return fallback;
      var raw = root.getAttribute(name);
      var num = raw === null || raw === '' ? null : Number(raw);
      return num !== null && isFinite(num) ? num : fallback;
    };
    var endpoint = root ? (root.getAttribute('data-endpoint') || '').trim() : '';
    // A leftover placeholder counts as "not configured" — it must not force the
    // page into the error state when DIRECT mode could serve real prices.
    if (/REPLACE-ME/i.test(endpoint)) endpoint = '';
    return {
      endpoint: endpoint,
      goldPremiumPct: read('data-gold-premium-pct', 95),
      goldOtherPct: read('data-gold-other-pct', 80),
      silverPremiumPct: read('data-silver-premium-pct', 65),
      silverOtherPct: read('data-silver-other-pct', 60)
    };
  }

  /* ------------------------------------------------------------- cache --- */

  function readCache() {
    try {
      var raw = window.sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || typeof entry.at !== 'number') return null;
      if (Date.now() - entry.at > CACHE_TTL_MS) return null;
      return isUsableContract(entry.data) ? entry.data : null;
    } catch (error) {
      return null; // Private mode / disabled storage: just fetch.
    }
  }

  function writeCache(data) {
    try {
      window.sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data: data }));
    } catch (error) {
      /* quota or disabled storage — caching is an optimisation, not a requirement */
    }
  }

  /* ------------------------------------------------------------- fetch --- */

  function fetchJson(url) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timeoutId = controller
      ? setTimeout(function () {
          controller.abort();
        }, FETCH_TIMEOUT_MS)
      : null;
    var options = { credentials: 'omit', mode: 'cors', cache: 'no-store' };
    if (controller) options.signal = controller.signal;

    return fetch(url, options)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (body) {
        if (timeoutId !== null) clearTimeout(timeoutId);
        return body;
      })
      .catch(function (error) {
        if (timeoutId !== null) clearTimeout(timeoutId);
        throw error;
      });
  }

  /* ------------------------------------------------- MODE A — DIRECT ----- */

  function buildDirect(config) {
    return Promise.all([fetchJson(API.gold), fetchJson(API.silver), fetchJson(API.fx)]).then(function (r) {
      var goldUsdOz = pickNumber(r[0], ['price', 'Price', 'rate', 'value']);
      var silverUsdOz = pickNumber(r[1], ['price', 'Price', 'rate', 'value']);
      var usdcad = pickNumber(r[2] && r[2].rates, ['CAD', 'cad']);

      if (!withinBounds(goldUsdOz, BOUNDS.gold_usd_oz)) throw new Error('gold out of bounds: ' + goldUsdOz);
      if (!withinBounds(silverUsdOz, BOUNDS.silver_usd_oz)) throw new Error('silver out of bounds: ' + silverUsdOz);
      if (!withinBounds(usdcad, BOUNDS.usdcad)) throw new Error('usdcad out of bounds: ' + usdcad);

      var goldCadOz = goldUsdOz * usdcad;
      var silverCadOz = silverUsdOz * usdcad;
      var goldCadG = goldCadOz / TROY_OUNCE_GRAMS;
      var silverCadG = silverCadOz / TROY_OUNCE_GRAMS;

      // The metal feed's own stamp, not fetch time — that is what makes the
      // weekend "markets closed" state detectable.
      var stamp = (r[0] && (r[0].updatedAt || r[0].updated_at)) || new Date().toISOString();
      var updatedAt = isFinite(Date.parse(stamp)) ? new Date(stamp).toISOString() : new Date().toISOString();

      var row = function (purity, cadPerGram, premiumPct, otherPct) {
        return {
          premium: roundUpCents(cadPerGram * purity * (premiumPct / 100)),
          other: roundUpCents(cadPerGram * purity * (otherPct / 100))
        };
      };

      return {
        updated_at: updatedAt,
        mode: 'direct',
        spot: {
          gold_cad_g: goldCadG,
          gold_cad_oz: goldCadOz,
          silver_cad_g: silverCadG,
          silver_cad_oz: silverCadOz,
          usdcad: usdcad
        },
        gold: GOLD_KARATS.map(function (k) {
          var p = row(k[1], goldCadG, config.goldPremiumPct, config.goldOtherPct);
          return { karat: k[0], purity: k[1], premium: p.premium, other: p.other };
        }),
        silver: SILVER_PURITIES.map(function (s) {
          var p = row(s[1], silverCadG, config.silverPremiumPct, config.silverOtherPct);
          return { purity_label: s[0], purity: s[1], premium: p.premium, other: p.other };
        }),
        status: { fresh: true, market_open: marketOpen(new Date()) }
      };
    });
  }

  /* ------------------------------------------------- MODE B — ENGINE ----- */

  function buildEngine(endpoint) {
    return fetchJson(endpoint).then(function (payload) {
      if (!isUsableContract(payload)) throw new Error('unexpected payload shape');
      payload.mode = 'engine';
      if (!payload.status) payload.status = {};
      if (typeof payload.status.market_open !== 'boolean') {
        payload.status.market_open = marketOpen(new Date());
      }
      return payload;
    });
  }

  function isUsableContract(payload) {
    return Boolean(
      payload &&
        typeof payload === 'object' &&
        typeof payload.updated_at === 'string' &&
        payload.spot &&
        Array.isArray(payload.gold) &&
        Array.isArray(payload.silver) &&
        payload.gold.length > 0
    );
  }

  /* -------------------------------------------------------------- feed --- */

  var feeds = Object.create(null);

  function Feed(key) {
    this.key = key;
    this.data = null;
    this.failed = false;
    this.lastFetchAt = 0;
    this.pending = null;
    this.subscribers = [];
  }

  function getFeed() {
    if (!feeds.main) feeds.main = new Feed('main');
    return feeds.main;
  }

  Feed.prototype.subscribe = function (fn) {
    if (this.subscribers.indexOf(fn) === -1) this.subscribers.push(fn);
  };

  Feed.prototype.unsubscribe = function (fn) {
    var i = this.subscribers.indexOf(fn);
    if (i !== -1) this.subscribers.splice(i, 1);
  };

  Feed.prototype.notify = function () {
    var list = this.subscribers.slice();
    for (var i = 0; i < list.length; i += 1) {
      try {
        list[i](this);
      } catch (error) {
        /* one bad root must not stop the others rendering */
      }
    }
  };

  Feed.prototype.load = function (maxAgeMs) {
    var self = this;
    if (this.pending) return;

    var age = Date.now() - this.lastFetchAt;
    var fresh = maxAgeMs ? Math.max(0, maxAgeMs - 1000) : 0;
    if (this.lastFetchAt && (this.data || this.failed) && age < fresh) {
      this.notify();
      return;
    }

    // Session cache first: navigating the site should not refetch.
    if (!this.data) {
      var cached = readCache();
      if (cached) {
        this.data = cached;
        this.failed = false;
        this.lastFetchAt = Date.now();
        this.notify();
        return;
      }
    }

    this.lastFetchAt = Date.now();
    var config = readConfig();
    var work = config.endpoint ? buildEngine(config.endpoint) : buildDirect(config);

    this.pending = work
      .then(function (data) {
        self.data = data;
        self.failed = false;
        writeCache(data);
      })
      .catch(function () {
        // Keep any previously good data rather than blanking the page; only a
        // first-load failure produces the call-us state.
        self.failed = !self.data;
      })
      .then(function () {
        self.pending = null;
        self.notify();
      });
  };

  /* ---------------------------------------------------------- instance --- */

  function LivePrices(root) {
    this.root = root;
    this.refreshMs = Math.max(15, Number(root.getAttribute('data-refresh-seconds')) || 60) * 1000;
    this.timezone = root.getAttribute('data-timezone') || 'America/Vancouver';
    // The brief's ladder: anything under two hours reads as live.
    // Sections already emit data-offline-hours (default 2); reuse it as the
    // live-window threshold so no section markup had to change.
    this.liveMs =
      (Number(root.getAttribute('data-live-hours')) ||
        Number(root.getAttribute('data-offline-hours')) ||
        2) * 60 * 60 * 1000;

    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-lp-card]'));
    this.spots = Array.prototype.slice.call(root.querySelectorAll('[data-lp-spot]'));
    this.priceCells = Array.prototype.slice.call(root.querySelectorAll('[data-lp-price]'));
    this.purityCells = Array.prototype.slice.call(root.querySelectorAll('[data-lp-purity-cell]'));
    this.metalSelect = root.querySelector('[data-lp-metal]');
    this.puritySelect = root.querySelector('[data-lp-purity]');
    this.weightInput = root.querySelector('[data-lp-weight]');
    this.premiumOut = root.querySelector('[data-lp-premium]');
    this.otherOut = root.querySelector('[data-lp-other]');
    this.otherLabel = root.querySelector('[data-lp-other-label]');
    this.statusText = root.querySelector('[data-lp-status-text]');

    this.feed = getFeed();
    this.data = null;
    this.loadedOnce = false;
    this.inView = false;
    this.timerId = null;
    this.observer = null;
    this.destroyed = false;

    this.onFeed = this.handleFeed.bind(this);
    this.feed.subscribe(this.onFeed);

    this.onVisibility = this.handleVisibility.bind(this);
    this.onCalcInput = this.renderCalculator.bind(this);
    this.onMetalChange = this.handleMetalChange.bind(this);

    this.bind();
    this.observe();
  }

  LivePrices.prototype.bind = function () {
    document.addEventListener('visibilitychange', this.onVisibility);
    if (this.metalSelect) this.metalSelect.addEventListener('change', this.onMetalChange);
    if (this.puritySelect) this.puritySelect.addEventListener('change', this.onCalcInput);
    if (this.weightInput) {
      this.weightInput.addEventListener('input', this.onCalcInput);
      this.weightInput.addEventListener('change', this.onCalcInput);
    }
  };

  LivePrices.prototype.observe = function () {
    var self = this;
    if (typeof IntersectionObserver !== 'function') {
      this.inView = true;
      this.load();
      return;
    }
    this.observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i += 1) self.inView = entries[i].isIntersecting;
        if (self.inView && !self.loadedOnce) self.load();
        else if (self.inView) self.catchUp();
        self.syncTimer();
      },
      { rootMargin: '300px 0px', threshold: 0 }
    );
    this.observer.observe(this.root);
  };

  LivePrices.prototype.handleVisibility = function () {
    if (!document.hidden) this.catchUp();
    this.syncTimer();
  };

  LivePrices.prototype.catchUp = function () {
    if (!this.loadedOnce || document.hidden || !this.inView) return;
    if (Date.now() - this.feed.lastFetchAt >= this.refreshMs) this.load();
  };

  LivePrices.prototype.syncTimer = function () {
    var shouldRun = this.loadedOnce && this.inView && !document.hidden && !this.destroyed;
    if (shouldRun && this.timerId === null) {
      var self = this;
      this.timerId = setInterval(function () {
        self.load();
      }, this.refreshMs);
    } else if (!shouldRun && this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  };

  LivePrices.prototype.load = function () {
    if (this.destroyed) return;
    this.feed.load(this.refreshMs);
  };

  LivePrices.prototype.handleFeed = function (feed) {
    if (this.destroyed) return;
    this.loadedOnce = true;
    if (feed.data && !feed.failed) {
      this.data = feed.data;
      this.render();
    } else {
      this.setState('offline');
    }
    this.syncTimer();
  };

  /* ------------------------------------------------------------ render --- */

  LivePrices.prototype.setState = function (state) {
    this.root.setAttribute('data-lp-state', state);
    if (state === 'offline') {
      this.resetCards();
      this.resetValues();
      this.resetCalculator();
    }
  };

  LivePrices.prototype.render = function () {
    var updatedAt = Date.parse(this.data.updated_at);
    if (!isFinite(updatedAt)) {
      this.setState('offline');
      return;
    }

    var age = Date.now() - updatedAt;
    var open = !(this.data.status && this.data.status.market_open === false);
    var label;

    if (age < this.liveMs) {
      // Under two hours old — live.
      this.setState('live');
      label = 'Updated ' + clockLabel(new Date(updatedAt), this.timezone) + ' PT';
    } else if (!open) {
      // Older, but the market is genuinely shut. Keep the prices: a weekend is
      // peak research time and blanking the page then is the worst outcome.
      this.setState('closed');
      label = 'Markets closed — showing last closing rates';
    } else {
      // Old data while the market is open means the feed really is behind.
      this.setState('stale');
      label = 'Last updated ' + clockLabel(new Date(updatedAt), this.timezone) + ' PT';
    }

    setText(this.statusText, label);

    this.renderCards();
    this.renderSpot();
    this.renderPriceCells();
    this.renderPurityCells();
    this.populatePurities();
    this.renderCalculator();
  };

  LivePrices.prototype.renderSpot = function () {
    var spot = (this.data && this.data.spot) || null;
    for (var i = 0; i < this.spots.length; i += 1) {
      var node = this.spots[i];
      var value = spot ? spot[node.getAttribute('data-lp-spot')] : null;
      setText(node, typeof value === 'number' && isFinite(value) ? money(value) : PLACEHOLDER);
    }
  };

  function cellKey(node, metal) {
    return metal === 'silver' ? node.getAttribute('data-purity-label') : node.getAttribute('data-karat');
  }

  LivePrices.prototype.renderPriceCells = function () {
    for (var i = 0; i < this.priceCells.length; i += 1) {
      var node = this.priceCells[i];
      var metal = node.getAttribute('data-metal');
      var tier = node.getAttribute('data-tier') === 'other' ? 'other' : 'premium';
      var row = this.rowFor(metal, cellKey(node, metal));
      var value = row ? row[tier] : null;

      if (typeof value === 'number' && isFinite(value)) {
        setText(node, money(value));
        node.setAttribute('data-lp-pending', 'false');
      } else {
        setText(node, PLACEHOLDER);
        node.setAttribute('data-lp-pending', 'true');
      }
    }
  };

  LivePrices.prototype.renderPurityCells = function () {
    for (var i = 0; i < this.purityCells.length; i += 1) {
      var node = this.purityCells[i];
      var row = this.rowFor(node.getAttribute('data-metal'), cellKey(node, node.getAttribute('data-metal')));
      if (!row || typeof row.purity !== 'number' || !isFinite(row.purity)) continue;
      setText(node, (Math.round(row.purity * 1000) / 10).toFixed(1) + '%');
    }
  };

  LivePrices.prototype.resetValues = function () {
    var i;
    for (i = 0; i < this.spots.length; i += 1) setText(this.spots[i], PLACEHOLDER);
    for (i = 0; i < this.priceCells.length; i += 1) {
      setText(this.priceCells[i], PLACEHOLDER);
      this.priceCells[i].setAttribute('data-lp-pending', 'true');
    }
  };

  LivePrices.prototype.findGold = function (karat) {
    var rows = (this.data && this.data.gold) || [];
    for (var i = 0; i < rows.length; i += 1) {
      if (Number(rows[i].karat) === Number(karat)) return rows[i];
    }
    return null;
  };

  LivePrices.prototype.findSilver = function (label) {
    var rows = (this.data && this.data.silver) || [];
    var wanted = normaliseLabel(label);
    var i;
    for (i = 0; i < rows.length; i += 1) {
      if (normaliseLabel(rows[i].purity_label) === wanted) return rows[i];
    }
    if (/925|sterling/.test(wanted)) {
      for (i = 0; i < rows.length; i += 1) {
        if (Math.abs(Number(rows[i].purity) - 0.925) < 0.001) return rows[i];
      }
    }
    return null;
  };

  LivePrices.prototype.rowFor = function (metal, key) {
    return metal === 'silver' ? this.findSilver(key) : this.findGold(key);
  };

  LivePrices.prototype.renderCards = function () {
    for (var i = 0; i < this.cards.length; i += 1) {
      var card = this.cards[i];
      var value = card.querySelector('[data-lp-value]');
      var metal = card.getAttribute('data-metal');
      var key = metal === 'silver' ? card.getAttribute('data-purity-label') : card.getAttribute('data-karat');
      var row = this.rowFor(metal, key);

      if (row && typeof row.premium === 'number' && isFinite(row.premium)) {
        setText(value, money(row.premium));
        card.setAttribute('data-lp-pending', 'false');
      } else {
        setText(value, PLACEHOLDER);
        card.setAttribute('data-lp-pending', 'true');
      }
    }
  };

  LivePrices.prototype.resetCards = function () {
    for (var i = 0; i < this.cards.length; i += 1) {
      setText(this.cards[i].querySelector('[data-lp-value]'), PLACEHOLDER);
      this.cards[i].setAttribute('data-lp-pending', 'true');
    }
  };

  LivePrices.prototype.currentMetal = function () {
    return this.metalSelect && this.metalSelect.value === 'silver' ? 'silver' : 'gold';
  };

  LivePrices.prototype.populatePurities = function () {
    if (!this.puritySelect || !this.data) return;
    var metal = this.currentMetal();
    var rows = metal === 'silver' ? this.data.silver : this.data.gold;
    var previous = this.puritySelect.value;
    var fragment = document.createDocumentFragment();
    var matched = false;

    for (var i = 0; i < rows.length; i += 1) {
      var row = rows[i];
      var value = metal === 'silver' ? String(row.purity_label) : String(row.karat);
      var label = metal === 'silver' ? String(row.purity_label).toUpperCase() : String(row.karat) + 'K';
      var option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      fragment.appendChild(option);
      if (value === previous) matched = true;
    }

    this.puritySelect.textContent = '';
    this.puritySelect.appendChild(fragment);
    if (matched) this.puritySelect.value = previous;
  };

  LivePrices.prototype.handleMetalChange = function () {
    this.populatePurities();
    this.renderCalculator();
  };

  LivePrices.prototype.renderCalculator = function () {
    var metal = this.currentMetal();
    setText(this.otherLabel, metal === 'silver' ? 'Other silver est.' : 'Other gold est.');

    if (!this.data || this.root.getAttribute('data-lp-state') === 'offline') {
      this.resetCalculator();
      return;
    }

    var grams = toNumber(this.weightInput ? this.weightInput.value : '');
    var row = this.rowFor(metal, this.puritySelect ? this.puritySelect.value : null);

    if (!row || grams === null || grams <= 0) {
      setText(this.premiumOut, PLACEHOLDER);
      setText(this.otherOut, PLACEHOLDER);
      return;
    }

    setText(this.premiumOut, money(row.premium * grams));
    setText(this.otherOut, money(row.other * grams));
  };

  LivePrices.prototype.resetCalculator = function () {
    setText(this.premiumOut, PLACEHOLDER);
    setText(this.otherOut, PLACEHOLDER);
  };

  /* --------------------------------------------------------- teardown --- */

  LivePrices.prototype.destroy = function () {
    this.destroyed = true;
    this.feed.unsubscribe(this.onFeed);
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.metalSelect) this.metalSelect.removeEventListener('change', this.onMetalChange);
    if (this.puritySelect) this.puritySelect.removeEventListener('change', this.onCalcInput);
    if (this.weightInput) {
      this.weightInput.removeEventListener('input', this.onCalcInput);
      this.weightInput.removeEventListener('change', this.onCalcInput);
    }
  };

  /* ------------------------------------------------------- lifecycle --- */

  function keyFor(root) {
    return root.getAttribute('data-section-id') || root.id || 'gsj-lp';
  }

  function mount(root) {
    var key = keyFor(root);
    if (instances[key]) instances[key].destroy();
    instances[key] = new LivePrices(root);
  }

  function mountAll(scope) {
    var roots = (scope || document).querySelectorAll(ROOT_SELECTOR);
    for (var i = 0; i < roots.length; i += 1) mount(roots[i]);
  }

  function destroyAll() {
    for (var key in instances) {
      if (Object.prototype.hasOwnProperty.call(instances, key)) {
        instances[key].destroy();
        delete instances[key];
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      mountAll(document);
    });
  } else {
    mountAll(document);
  }

  window.addEventListener('pagehide', destroyAll);

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) mountAll(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector) mountAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (!event.target || !event.target.querySelector) return;
    var root = event.target.querySelector(ROOT_SELECTOR);
    if (!root) return;
    var key = keyFor(root);
    if (instances[key]) {
      instances[key].destroy();
      delete instances[key];
    }
  });

  /* Exposed for QA only — lets a test drive the contract without the network. */
  window.GSJLivePrices = {
    _buildDirect: buildDirect,
    _readConfig: readConfig,
    _marketOpen: marketOpen,
    _roundUpCents: roundUpCents,
    _feed: getFeed
  };
})();
